import { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';

const API = import.meta.env.VITE_API_URL || '/api';

type Tab = 'comercio' | 'usuarios';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  taxRate?: string;
  currency?: string;
  modules?: string[];
}

interface User {
  id: string;
  email: string;
  name?: string;
  role: string;
  active: boolean;
  phone?: string | null;
}

interface Role {
  role: string;
  permissions: string[];
}

const tabs: { id: Tab; label: string; icon: string }[] = [
  { id: 'comercio', label: 'Mi Comercio', icon: '🏪' },
  { id: 'usuarios', label: 'Usuarios', icon: '👥' },
];

const currencies = [
  { value: 'ARS', label: 'ARS ($)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'BRL', label: 'BRL (R$)' },
];

const modules = ['core', 'stock', 'pos', 'suppliers', 'customers', 'billing', 'reports'];

const roleLabels: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  cashier: 'Cajero',
};

export function Admin() {
  const me = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } })();
  const isManager = me?.role === 'manager';
  const [tab, setTab] = useState<Tab>('comercio');

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loadingTenant, setLoadingTenant] = useState(false);
  const [errorTenant, setErrorTenant] = useState<string | null>(null);

  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [errorUsers, setErrorUsers] = useState<string | null>(null);

  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);

  const [userForm, setUserForm] = useState({ email: '', password: '', name: '', role: 'cashier', google: false, phone: '' });
  const [myTenantForm, setMyTenantForm] = useState({ name: '', taxRate: '', currency: 'ARS' });
  // Catálogo general: rubro del que este comercio toma productos ('' = ninguna)
  const [catalogVerticals, setCatalogVerticals] = useState<{ id: string; code: string; name: string }[]>([]);
  const [catalogVerticalCode, setCatalogVerticalCode] = useState('');
  const [catalogSaving, setCatalogSaving] = useState(false);
  const [catalogMsg, setCatalogMsg] = useState<string | null>(null);

  // ARCA: facturación electrónica
  const [arcaStatus, setArcaStatus] = useState<{
    arcaEnabled: boolean; arcaCuit: string | null; arcaCuitValid: boolean;
    arcaPointOfSale: number; arcaProduction: boolean; arcaIvaCondition: number; hasCredentials: boolean;
  } | null>(null);
  const [arcaForm, setArcaForm] = useState({ arcaCuit: '', arcaPointOfSale: 1, arcaProduction: false, arcaIvaCondition: 0, arcaEnabled: false });
  const [arcaKey, setArcaKey] = useState('');
  const [arcaCert, setArcaCert] = useState('');
  const [arcaLoading, setArcaLoading] = useState(false);
  const [arcaError, setArcaError] = useState<string | null>(null);
  const [arcaSaving, setArcaSaving] = useState(false);
  const [arcaTesting, setArcaTesting] = useState(false);
  const [arcaMessage, setArcaMessage] = useState<string | null>(null);

  const [saved, setSaved] = useState(false);
  const [userError, setUserError] = useState('');
  const [myTenantError, setMyTenantError] = useState('');

  const [submittingUser, setSubmittingUser] = useState(false);
  const [submittingMyTenant, setSubmittingMyTenant] = useState(false);

  const [logoUploading, setLogoUploading] = useState(false);
  const [hasLogo, setHasLogo] = useState(false);

  const token = localStorage.getItem('accessToken');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
  };

  async function loadMyTenant() {
    setLoadingTenant(true);
    setErrorTenant(null);
    try {
      const res = await fetch(`${API}/admin/tenant`, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al cargar el comercio');
      }
      const d = await res.json();
      const data = d.data as Tenant;
      setTenant(data);
      setMyTenantForm({
        name: data.name || '',
        taxRate: data.taxRate || '21',
        currency: data.currency || 'ARS',
      });
      const logoRes = await fetch(`${API}/admin/tenant/logo`, { headers });
      setHasLogo(logoRes.ok);

      const catRes = await fetch(`${API}/admin/tenant/catalog`, { headers });
      if (catRes.ok) {
        const cd = await catRes.json().catch(() => ({}));
        const verticals = (cd.data?.verticals ?? []) as { id: string; code: string; name: string }[];
        setCatalogVerticals(verticals);
        const selected = verticals.find((v) => v.id === cd.data?.selectedVerticalId);
        setCatalogVerticalCode(selected?.code ?? '');
      }
    } catch (e: any) {
      setErrorTenant(e.message || 'Error al cargar el comercio');
    } finally {
      setLoadingTenant(false);
    }
  }

  async function loadArcaStatus() {
    try {
      const res = await fetch(`${API}/admin/arca/status`, { headers });
      if (!res.ok) return;
      const d = await res.json();
      const s = d.data;
      setArcaStatus(s);
      setArcaForm({
        arcaCuit: s.arcaCuit || '',
        arcaPointOfSale: s.arcaPointOfSale || 1,
        arcaProduction: !!s.arcaProduction,
        arcaIvaCondition: s.arcaIvaCondition || 0,
        arcaEnabled: !!s.arcaEnabled,
      });
    } catch { /* noop */ }
  }

  async function saveArcaConfig() {
    setArcaSaving(true);
    setArcaError(null);
    setArcaMessage(null);
    try {
      const res = await fetch(`${API}/admin/arca/config`, {
        method: 'PUT', headers,
        body: JSON.stringify(arcaForm),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al guardar config ARCA');
      setArcaMessage('Config ARCA guardada');
      await loadArcaStatus();
    } catch (e: any) {
      setArcaError(e.message || 'Error');
    } finally {
      setArcaSaving(false);
    }
  }

  async function saveArcaConfigWithEnable(enabled: boolean) {
    setArcaSaving(true);
    setArcaError(null);
    setArcaMessage(null);
    try {
      const res = await fetch(`${API}/admin/arca/config`, {
        method: 'PUT', headers,
        body: JSON.stringify({ ...arcaForm, arcaEnabled: enabled }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al guardar config ARCA');
      setArcaMessage(enabled ? 'ARCA habilitada — las próximas ventas emitirán factura fiscal' : 'ARCA deshabilitada');
      await loadArcaStatus();
    } catch (e: any) {
      setArcaError(e.message || 'Error');
    } finally {
      setArcaSaving(false);
    }
  }

  async function saveArcaCredentials() {
    setArcaSaving(true);
    setArcaError(null);
    setArcaMessage(null);
    try {
      const res = await fetch(`${API}/admin/arca/credentials`, {
        method: 'PUT', headers,
        body: JSON.stringify({ key: arcaKey, cert: arcaCert }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al subir credenciales');
      setArcaMessage('Certificado subido y cifrado');
      setArcaKey('');
      setArcaCert('');
      await loadArcaStatus();
    } catch (e: any) {
      setArcaError(e.message || 'Error');
    } finally {
      setArcaSaving(false);
    }
  }

  async function testArca() {
    setArcaTesting(true);
    setArcaError(null);
    setArcaMessage(null);
    try {
      const res = await fetch(`${API}/admin/arca/test`, {
        method: 'POST', headers,
        body: JSON.stringify({}),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al probar conexión');
      setArcaMessage(`ARCA conectado OK (${d.data?.config?.production ? 'producción' : 'homologación'}, CUIT ${d.data?.config?.cuit})`);
    } catch (e: any) {
      setArcaError(e.message || 'Error');
    } finally {
      setArcaTesting(false);
    }
  }

  async function loadUsers() {
    setLoadingUsers(true);
    setErrorUsers(null);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        fetch(`${API}/admin/users`, { headers }),
        fetch(`${API}/admin/roles`, { headers }),
      ]);
      if (!usersRes.ok) {
        const err = await usersRes.json().catch(() => ({}));
        throw new Error(err.error || 'Error al cargar usuarios');
      }
      const usersData = await usersRes.json();
      setUsers((usersData.data as User[]) || []);

      if (rolesRes.ok) {
        const rolesData = await rolesRes.json();
        setRoles((rolesData.data as Role[]) || []);
      } else {
        setRoles([]);
      }
    } catch (e: any) {
      setErrorUsers(e.message || 'Error al cargar usuarios');
    } finally {
      setLoadingUsers(false);
    }
  }

  useEffect(() => {
    loadMyTenant();
    loadArcaStatus();
  }, []);

  function onTabChange(next: Tab) {
    setTab(next);
    if (next === 'usuarios' && users.length === 0) loadUsers();
  }

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    const formData = new FormData();
    formData.append('logo', file);
    try {
      const res = await fetch(`${API}/admin/tenant/logo`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
        },
        body: formData,
      });
      if (res.ok) {
        setHasLogo(true);
        window.location.reload();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Error al subir el logo');
      }
    } catch (e: any) {
      alert(e.message || 'Error al subir el logo');
    } finally {
      setLogoUploading(false);
    }
  }

  async function handleLogoDelete() {
    try {
      const delHeaders = { ...headers } as Record<string, string>;
      delete delHeaders['Content-Type'];
      const res = await fetch(`${API}/admin/tenant/logo`, { method: 'DELETE', headers: delHeaders });
      if (res.ok) {
        setHasLogo(false);
        window.location.reload();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Error al eliminar el logo');
      }
    } catch (e: any) {
      alert(e.message || 'Error al eliminar el logo');
    }
  }

  async function saveCatalogVertical(code: string) {
    setCatalogSaving(true);
    setCatalogMsg(null);
    try {
      const res = await fetch(`${API}/admin/tenant/catalog`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ verticalCode: code || null }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al guardar el rubro');
      setCatalogVerticalCode(code);
      setCatalogMsg(code ? '✓ Rubro guardado' : '✓ Catálogo desactivado');
      setTimeout(() => setCatalogMsg(null), 2500);
    } catch (e: any) {
      setCatalogMsg(e.message || 'Error al guardar el rubro');
    } finally {
      setCatalogSaving(false);
    }
  }

  async function saveMyTenant(e: React.FormEvent) {
    e.preventDefault();
    setSubmittingMyTenant(true);
    setMyTenantError('');
    try {
      const res = await fetch(`${API}/admin/tenant`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(myTenantForm),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al guardar');
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      loadMyTenant();
    } catch (e: any) {
      setMyTenantError(e.message || 'Error al guardar');
    } finally {
      setSubmittingMyTenant(false);
    }
  }

  async function saveUser(e: React.FormEvent) {
    e.preventDefault();
    setSubmittingUser(true);
    setUserError('');
    try {
      let res: Response;
      if (editingUserId) {
        const body: Record<string, string> = { name: userForm.name, role: userForm.role };
        if (userForm.password) body.password = userForm.password;
        if (userForm.phone !== undefined) body.phone = userForm.phone;
        res = await fetch(`${API}/admin/users/${editingUserId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API}/admin/users`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ ...userForm, phone: userForm.phone || undefined }),
        });
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al guardar usuario');
      }
      setShowUserForm(false);
      setEditingUserId(null);
      setUserForm({ email: '', password: '', name: '', role: 'cashier', google: false, phone: '' });
      loadUsers();
    } catch (e: any) {
      setUserError(e.message || 'Error al guardar usuario');
    } finally {
      setSubmittingUser(false);
    }
  }

  function editUser(u: User) {
    setEditingUserId(u.id);
    setUserForm({ email: u.email, password: '', name: u.name || '', role: u.role, google: false, phone: u.phone || '' });
    setUserError('');
    setShowUserForm(true);
  }

  async function toggleUser(u: User) {
    try {
      const res = await fetch(`${API}/admin/users/${u.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ active: !u.active }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al cambiar estado');
      }
      loadUsers();
    } catch (e: any) {
      alert(e.message || 'Error al cambiar estado');
    }
  }

  async function deleteUser(id: string) {
    try {
      const delHeaders = { ...headers } as Record<string, string>;
      delete delHeaders['Content-Type'];
      const res = await fetch(`${API}/admin/users/${id}`, { method: 'DELETE', headers: delHeaders });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al eliminar usuario');
      }
      loadUsers();
    } catch (e: any) {
      alert(e.message || 'Error al eliminar usuario');
    }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Administración</h1>
        <Link
          to="/"
          className="inline-flex items-center justify-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 min-h-[44px] px-2 rounded transition-colors"
        >
          ← Volver al Dashboard
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b overflow-x-auto mb-6">
        {tabs.filter((t) => !(isManager && t.id === 'usuarios')).map((t) => (
          <button
            key={t.id}
            onClick={() => onTabChange(t.id)}
            className={`flex items-center gap-2 px-4 py-3 min-h-[48px] text-sm sm:text-base font-medium whitespace-nowrap border-b-2 transition-colors ${
              tab === t.id
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            <span>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {/* MI COMERCIO */}
      {tab === 'comercio' && (
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <h2 className="text-lg font-bold mb-4">🏪 Configuración del Comercio</h2>

          {loadingTenant ? (
            <div className="space-y-4 animate-pulse">
              <div className="h-24 bg-gray-200 rounded-lg" />
              <div className="h-10 bg-gray-200 rounded" />
              <div className="h-10 bg-gray-200 rounded" />
            </div>
          ) : errorTenant ? (
            <div className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 p-4 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <span className="text-sm font-medium">⚠️ {errorTenant}</span>
              <button
                onClick={loadMyTenant}
                className="px-4 py-2 bg-red-700 text-white rounded-lg text-sm font-medium hover:bg-red-800 transition-colors min-h-[44px]"
              >
                Reintentar
              </button>
            </div>
          ) : tenant ? (
            <>
              {/* Logo */}
              <div className="flex flex-col sm:flex-row gap-4 items-start mb-6 p-4 border rounded-lg bg-gray-50 dark:bg-gray-700">
                {hasLogo ? (
                  <img
                    src={`${API}/tenant-logo/${localStorage.getItem('tenantSlug') || ''}?t=${Date.now()}`}
                    alt="Logo actual"
                    className="w-16 h-16 object-contain rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-400 dark:text-gray-400 text-2xl bg-white dark:bg-gray-800">
                    🏪
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Logo del Comercio</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-3">
                    PNG, JPEG, WebP o SVG. Máx 2MB. Tamaño recomendado: 128x128px.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <label className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors min-h-[44px] cursor-pointer">
                      {logoUploading ? 'Subiendo...' : hasLogo ? '🔄 Cambiar' : '📤 Subir Logo'}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        onChange={handleLogoUpload}
                        className="hidden"
                        disabled={logoUploading}
                      />
                    </label>
                    {hasLogo && (
                      <button
                        type="button"
                        onClick={handleLogoDelete}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 text-red-700 dark:text-red-300 rounded-lg text-sm font-medium transition-colors min-h-[44px]"
                      >
                        🗑️ Eliminar
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <form onSubmit={saveMyTenant} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nombre del Comercio *</label>
                    <input
                      type="text"
                      value={myTenantForm.name}
                      onChange={(e) => setMyTenantForm({ ...myTenantForm, name: e.target.value })}
                      className="w-full"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Slug (identificador)</label>
                    <input
                      type="text"
                      value={tenant.slug || ''}
                      disabled
                      className="w-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 dark:dark:text-gray-400"
                    />
                    <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">El slug no se puede cambiar</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Tasa de IVA (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={myTenantForm.taxRate}
                      onChange={(e) => setMyTenantForm({ ...myTenantForm, taxRate: e.target.value })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Moneda</label>
                    <select
                      value={myTenantForm.currency}
                      onChange={(e) => setMyTenantForm({ ...myTenantForm, currency: e.target.value })}
                      className="w-full"
                    >
                      {currencies.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Catálogo general (rubro)</label>
                    <select
                      value={catalogVerticalCode}
                      disabled={catalogSaving}
                      onChange={(e) => saveCatalogVertical(e.target.value)}
                      className="w-full"
                    >
                      <option value="">Ninguna</option>
                      {catalogVerticals.map((v) => (
                        <option key={v.code} value={v.code}>{v.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">
                      Base de productos compartida para buscar y copiar. Es de solo lectura: lo que agregás se copia a tu lista.
                    </p>
                    {catalogMsg && (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">{catalogMsg}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Módulos Activos</label>
                  <div className="flex flex-wrap gap-2">
                    {modules.map((m) => (
                      <span
                        key={m}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          tenant.modules?.includes(m)
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 dark:dark:text-gray-400'
                        }`}
                      >
                        {tenant.modules?.includes(m) ? '✓' : '○'} {m}
                      </span>
                    ))}
                  </div>
                </div>

                {myTenantError && (
                  <p className="text-red-600 text-sm">{myTenantError}</p>
                )}

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={submittingMyTenant}
                    className="w-full sm:w-auto px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white rounded-lg font-semibold transition-colors min-h-[48px]"
                  >
                    {submittingMyTenant ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                  {saved && <span className="text-green-600 dark:text-green-400 text-sm font-medium">✓ Guardado</span>}
                </div>
              </form>

              <div className="mt-8 pt-6 border-t">
                <h3 className="text-base font-bold mb-3">💾 Datos del Comercio</h3>
                <Link
                  to="/backups"
                  className="flex items-center justify-between gap-4 bg-white dark:bg-gray-800 border rounded-lg p-4 hover:border-blue-400 hover:shadow-sm transition-all"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">💾</span>
                    <div>
                      <p className="font-semibold text-gray-800 dark:text-gray-100">Backups</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Crear, descargar y restaurar copias de seguridad.</p>
                    </div>
                  </div>
                  <span className="text-blue-600 dark:text-blue-400 font-medium text-sm">Ir →</span>
                </Link>
              </div>

              {/* ARCA — FACTURACIÓN ELECTRÓNICA */}
              <div className="mt-8 pt-6 border-t">
                <h3 className="text-base font-bold mb-1">🧾 Facturación Electrónica (ARCA)</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Emite facturas fiscales (A/B/C) al vender desde el POS. Configurá CUIT y certificado para habilitarla.
                </p>

                {arcaStatus && (
                  <div className="flex flex-wrap gap-2 mb-4 text-xs">
                    <span className={`px-3 py-1 rounded-full font-medium ${arcaStatus.arcaEnabled ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>
                      {arcaStatus.arcaEnabled ? '✓ Habilitada' : '○ Deshabilitada'}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                      CUIT: {arcaStatus.arcaCuit || '—'} {arcaStatus.arcaCuit && !arcaStatus.arcaCuitValid && <span className="text-red-600">(inválido)</span>}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                      Pto. Venta: {arcaStatus.arcaPointOfSale}
                    </span>
                    <span className={`px-3 py-1 rounded-full font-medium ${arcaStatus.arcaProduction ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300' : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'}`}>
                      {arcaStatus.arcaProduction ? 'Producción' : 'Homologación'}
                    </span>
                    <span className={`px-3 py-1 rounded-full font-medium ${arcaStatus.hasCredentials ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'}`}>
                      {arcaStatus.hasCredentials ? '✓ Certificado cargado' : '✗ Sin certificado'}
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">CUIT *</label>
                    <input
                      type="text"
                      placeholder="20112233445"
                      maxLength={11}
                      value={arcaForm.arcaCuit}
                      onChange={(e) => setArcaForm({ ...arcaForm, arcaCuit: e.target.value.replace(/\D/g, '') })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Punto de Venta</label>
                    <input
                      type="number"
                      min={1}
                      max={9999}
                      value={arcaForm.arcaPointOfSale}
                      onChange={(e) => setArcaForm({ ...arcaForm, arcaPointOfSale: Number(e.target.value) || 1 })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Condición de IVA del comercio</label>
                    <select
                      value={arcaForm.arcaIvaCondition}
                      onChange={(e) => setArcaForm({ ...arcaForm, arcaIvaCondition: Number(e.target.value) })}
                      className="w-full"
                    >
                      <option value={0}>Sin especificar</option>
                      <option value={1}>Responsable Inscripto (emite A/B/C)</option>
                      <option value={4}>Monotributo (emite C)</option>
                    </select>
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={arcaForm.arcaProduction}
                        onChange={(e) => setArcaForm({ ...arcaForm, arcaProduction: e.target.checked })}
                        className="w-auto"
                      />
                      <span className="text-sm font-medium">Modo producción</span>
                    </label>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <button
                    type="button"
                    onClick={saveArcaConfig}
                    disabled={arcaSaving}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg text-sm font-medium transition-colors min-h-[44px]"
                  >
                    {arcaSaving ? 'Guardando...' : 'Guardar Configuración'}
                  </button>
                  <label className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium transition-colors min-h-[44px] cursor-pointer">
                    {arcaStatus?.arcaEnabled ? 'Deshabilitar ARCA' : 'Habilitar ARCA'}
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={arcaStatus?.arcaEnabled}
                      onChange={(e) => {
                        saveArcaConfigWithEnable(e.target.checked);
                      }}
                    />
                  </label>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                  <p className="text-sm font-medium mb-2">📜 Certificado digital (CSR + clave privada)</p>
                  <textarea
                    placeholder="-----BEGIN CERTIFICATE----- ..."
                    value={arcaCert}
                    onChange={(e) => setArcaCert(e.target.value)}
                    rows={3}
                    className="w-full font-mono text-xs mb-2"
                  />
                  <textarea
                    placeholder="-----BEGIN PRIVATE KEY----- ..."
                    value={arcaKey}
                    onChange={(e) => setArcaKey(e.target.value)}
                    rows={3}
                    className="w-full font-mono text-xs mb-2"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={saveArcaCredentials}
                      disabled={arcaSaving}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white rounded-lg text-sm font-medium transition-colors min-h-[44px]"
                    >
                      {arcaSaving ? 'Subiendo...' : 'Subir Certificado'}
                    </button>
                    <button
                      type="button"
                      onClick={testArca}
                      disabled={arcaTesting}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white rounded-lg text-sm font-medium transition-colors min-h-[44px]"
                    >
                      {arcaTesting ? 'Probando...' : '🔌 Probar Conexión'}
                    </button>
                  </div>
                </div>

                {arcaError && <p className="text-red-600 text-sm mb-2">⚠️ {arcaError}</p>}
                {arcaMessage && <p className="text-green-600 dark:text-green-400 text-sm mb-2">✓ {arcaMessage}</p>}
              </div>
            </>
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 py-8">No se encontró el comercio.</p>
          )}
        </section>
      )}

      {/* USUARIOS */}
      {tab === 'usuarios' && (
        <section>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-lg font-bold">👥 Usuarios del Comercio</h2>
            {!isManager && (
            <button
              onClick={() => {
                setEditingUserId(null);
                setUserForm({ email: '', password: '', name: '', role: 'cashier', google: false, phone: '' });
                setUserError('');
                setShowUserForm(!showUserForm);
              }}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold shadow transition-colors min-h-[56px]"
            >
              {showUserForm && !editingUserId ? 'Cancelar' : '+ Nuevo Usuario'}
            </button>
            )}
          </div>

          {showUserForm && (
            <form onSubmit={saveUser} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 mb-6">
              <h3 className="font-bold mb-4">
                {editingUserId ? '✏️ Editar Usuario' : '👤 Nuevo Usuario'}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    className="w-full"
                    required
                    disabled={!!editingUserId}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nombre</label>
                  <input
                    type="text"
                    value={userForm.name}
                    onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Teléfono (WhatsApp)</label>
                  <input
                    type="tel"
                    value={userForm.phone}
                    onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                    className="w-full"
                    placeholder="+5491127043909"
                    inputMode="tel"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Opcional. Con código de país (E.164). Usado para alertas por WhatsApp.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {userForm.google && !editingUserId ? 'Cuenta Google Workspace' : (editingUserId ? 'Nueva Password' : 'Password *')}
                  </label>
                  {userForm.google && !editingUserId ? (
                    <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-2 min-h-[42px]">
                      🔑 Sin password — valida contra Google OAuth
                    </p>
                  ) : (
                    <input
                      type="password"
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      className="w-full"
                      placeholder={editingUserId ? '(dejar vacío para no cambiar)' : ''}
                      required={!editingUserId && !userForm.google}
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Rol</label>
                  <select
                    value={userForm.role}
                    onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    className="w-full"
                  >
                    {!isManager && <option value="admin">Dueño (acceso completo)</option>}
                    <option value="manager">Manager (productos, proveedores, OCs, reportes)</option>
                    <option value="cashier">Cajero (solo POS y caja)</option>
                  </select>
                </div>
                {!editingUserId && (
                  <div className="sm:col-span-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={userForm.google}
                        onChange={(e) => setUserForm({ ...userForm, google: e.target.checked })}
                        className="h-4 w-4"
                      />
                      Usuario de Google Workspace (sin contraseña, valida contra Google OAuth)
                    </label>
                  </div>
                )}
              </div>
              {userError && <p className="text-red-600 text-sm mt-3">{userError}</p>}
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <button
                  type="submit"
                  disabled={submittingUser}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white rounded-lg font-medium transition-colors min-h-[44px]"
                >
                  {submittingUser ? 'Guardando...' : editingUserId ? 'Guardar' : 'Crear Usuario'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowUserForm(false);
                    setEditingUserId(null);
                  }}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 dark:text-gray-200 rounded-lg font-medium transition-colors min-h-[44px]"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}

          {loadingUsers ? (
            <div className="space-y-3 animate-pulse">
              <div className="h-20 bg-gray-200 rounded-lg" />
              <div className="h-20 bg-gray-200 rounded-lg" />
            </div>
          ) : errorUsers ? (
            <div className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 p-4 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <span className="text-sm font-medium">⚠️ {errorUsers}</span>
              <button
                onClick={loadUsers}
                className="px-4 py-2 bg-red-700 text-white rounded-lg text-sm font-medium hover:bg-red-800 transition-colors min-h-[44px]"
              >
                Reintentar
              </button>
            </div>
          ) : users.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
              <p className="text-4xl mb-3">👤</p>
              <p className="text-gray-600 dark:text-gray-300 font-medium">No hay usuarios en este comercio.</p>
              <button
                onClick={() => {
                  setEditingUserId(null);
setUserForm({ email: '', password: '', name: '', role: 'cashier', google: false, phone: '' });
                  setShowUserForm(true);
                }}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors min-h-[44px]"
              >
                Crear el primero
              </button>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="grid grid-cols-1 gap-3 sm:hidden">
                {users.map((u) => (
                  <div key={u.id} className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 ${!u.active ? 'opacity-60' : ''}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-semibold text-gray-800 dark:text-gray-100">{u.name || u.email}</h3>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          u.role === 'admin'
                            ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                            : u.role === 'manager'
                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        {roleLabels[u.role] || u.role}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">{u.email}</p>
                    {u.phone && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">📱 {u.phone}</p>
                    )}
                    <div className="flex gap-2 mb-3">
                      <button
                        onClick={() => toggleUser(u)}
                        className={`px-3 py-2 rounded-lg text-xs font-medium min-h-[44px] transition-colors ${
                          u.active
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 hover:bg-green-200'
                            : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 hover:bg-red-200'
                        }`}
                      >
                        {u.active ? 'Activo' : 'Inactivo'}
                      </button>
                    </div>
                    {!isManager && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => editUser(u)}
                        className="flex-1 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium min-h-[44px] transition-colors"
                      >
                        ✏️ Editar
                      </button>
                      <button
                        onClick={() => deleteUser(u.id)}
                        className="flex-1 px-3 py-2 bg-red-50 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-700 dark:text-red-300 rounded-lg text-sm font-medium min-h-[44px] transition-colors"
                      >
                        🗑️ Eliminar
                      </button>
                    </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Nombre</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Teléfono</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Rol</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">Estado</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className={`border-t ${!u.active ? 'opacity-50' : ''}`}>
                        <td className="px-4 py-3 font-medium">{u.email}</td>
                        <td className="px-4 py-3">{u.name || '-'}</td>
                        <td className="px-4 py-3">{u.phone || '-'}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              u.role === 'admin'
                                ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                                : u.role === 'manager'
                                ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                            }`}
                          >
                            {roleLabels[u.role] || u.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => toggleUser(u)}
                            className={`px-3 py-2 rounded-lg text-xs font-medium min-h-[44px] transition-colors ${
                              u.active
                                ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 hover:bg-green-200'
                                : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 hover:bg-red-200'
                            }`}
                          >
                            {u.active ? 'Activo' : 'Inactivo'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {!isManager && (
                          <>
                          <button
                            onClick={() => editUser(u)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 font-medium text-sm min-h-[44px] px-2 rounded transition-colors"
                          >
                            ✏️ Editar
                          </button>
                          <button
                            onClick={() => deleteUser(u.id)}
                            className="text-red-600 hover:text-red-800 font-medium text-sm min-h-[44px] px-2 rounded transition-colors"
                          >
                            🗑️ Eliminar
                          </button>
                          </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {roles.length > 0 && (
                <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-5">
                  <h3 className="font-bold mb-3 text-sm">📋 Permisos por Rol</h3>
                  <div className="space-y-2">
                    {roles.map((r) => (
                      <div key={r.role} className="text-sm text-gray-600 dark:text-gray-300">
                        <span className="font-medium capitalize">{r.role}:</span>{' '}
                        {r.permissions.join(', ')}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
